import Head from 'next/head';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Box, Container, LinearProgress, Link } from '@mui/material';
import { sendApiRequest } from '../../lib/api';
import Custom404 from '../../components/common/Custom404';

export async function getServerSideProps(context) {
  const { id } = context.query;

  let response;

  try {
    response = await sendApiRequest({
      path: `/jobs/detail?_id=${id}&public=true`,
      method: 'GET',
    });
  } catch (e) {
    return {
      props: {
        initialJob: null,
        initialCompany: null,
      },
    };
  }

  if (!response.error) {
    return {
      props: {
        initialJob: response.job,
        initialCompany: response.company,
      },
    };
  }

  return {
    props: {
      initialJob: null,
      initialCompany: null,
    },
  };
}

const Job = ({ initialJob, initialCompany }: { initialJob: any; initialCompany: any }) => {
  const [company, setCompany] = useState(initialCompany);
  const [job, setJob] = useState(initialJob);
  const [isLoading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const router = useRouter();
  const id = router.query.id;

  const getJob = () => {
    if (!id) {
      return;
    }

    setLoading(true);

    sendApiRequest({
      path: `/jobs/detail?_id=${id}&public=true`,
      method: 'GET',
    })
      .then((response: any) => {
        setLoading(false);

        if (!response.error) {
          setJob(response.job);
          setCompany(response.company);
        }
      })
      .catch((e) => {
        if (e.message.includes('Job not found')) {
          setNotFound(true);
        }

        setLoading(false);
        setJob(null);
        setCompany(null);
      });
  };

  useEffect(() => {
    getJob();
  }, [id]);

  if (notFound) {
    return <Custom404 />;
  }

  if (!job) {
    return null;
  }

  const es = job.estimatedSalary || {};
  const minSalary = es.minValue;
  const maxSalary = es.maxValue;

  let metaDescription = `${job.title} at ${company.name}`;

  if (job.location) {
    metaDescription += `, ${job.location}`;
  }

  if (job.datePosted) {
    metaDescription += `, ${job.datePosted}`;
  }

  if (minSalary && maxSalary) {
    metaDescription += `, ${minSalary}$ - ${maxSalary}$`;
  }

  const title = `${job.title} at ${company.name} at workinbiotech.com`;

  let ldJson: any;

  if (job && job.location === 'Remote') {
    ldJson = {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      title: job.title,
      description: job.htmlDescription,
      datePosted: moment(job.datePosted).toISOString(),
      validThrough: moment(job.validThrough).toISOString(),
      employmentType: job.employmentType,
      hiringOrganization: {
        '@type': 'Organization',
        name: company.name,
        sameAs: company.websiteUrl,
        logo: company.logo,
      },
      applicantLocationRequirements: {
        '@type': 'Country',
        name: 'USA',
      },
      jobLocationType: 'TELECOMMUTE',
      estimatedSalary: {
        '@type': 'MonetaryAmount',
        name: 'base',
        currency: 'USD',
        minValue: parseInt(((minSalary || 0) + '000') as string, 10),
        maxValue: parseInt(((maxSalary || 0) + '000') as string, 10),
      },
    };
  } else {
    ldJson = {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      title: job.title,
      description: job.htmlDescription,
      datePosted: moment(job.datePosted).toISOString(),
      validThrough: moment(job.validThrough).toISOString(),
      employmentType: job.employmentType,
      hiringOrganization: {
        '@type': 'Organization',
        name: company.name,
        sameAs: company.websiteUrl,
        logo: company.logo,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: job.location,
          addressRegion: job.state,
          addressCountry: 'US',
        },
      },
      estimatedSalary: {
        '@type': 'MonetaryAmount',
        name: 'base',
        currency: 'USD',
        minValue: parseInt(((minSalary || 0) + '000') as string, 10),
        maxValue: parseInt(((maxSalary || 0) + '000') as string, 10),
      },
    };
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
      </Head>

      <Container sx={{ marginTop: '20px', color: '#515357' }}>
        {isLoading ? <LinearProgress /> : null}

        <div className="header">
          <div className="content">
            <img src={company.logo} style={{ maxHeight: '80px' }} />

            <div style={{ marginTop: '10px' }}>
              <Link href={`/companies/${job.companySlug}`} target="_blank">
                All Jobs
              </Link>
            </div>

            <h1 className="title">{job.title}</h1>

            <div>
              <strong>{job.location}</strong>
            </div>

            <div>
              <strong>{job.locationType}</strong>
            </div>
          </div>
        </div>
      </Container>

      <Box bgcolor="#f9f9f9" sx={{ padding: '40px 20px', margin: '40px 0px', color: '#515357' }}>
        <Container>
          <div dangerouslySetInnerHTML={{ __html: job.description }} />

          <div>
            {minSalary && maxSalary ? (
              <div>
                {formatter.format(minSalary)} - {formatter.format(maxSalary)} USD/year
              </div>
            ) : null}
          </div>
        </Container>
      </Box>
    </>
  );
};

export default Job;
